<script setup lang="ts">
import { ref, watch, toRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { StoreSettingsService } from '@/services/store-settings.services';
import { useBodyScrollLock } from '@/composables/useBodyScrollLock';
import type { IModuleDef } from './ModulesManagementPage.vue';

const props = defineProps<{
  open: boolean;
  store: any | null;
  module: IModuleDef | null;
  currentValue: boolean;
}>();
const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'toggled'): void;
}>();

const { t } = useI18n({ useScope: 'global' });
const router = useRouter();

function goToStore() {
  if (!props.store?.id) return;
  emit('close');
  router.push(`/commerces/${props.store.id}?tab=modules`);
}

useBodyScrollLock(toRef(props, 'open'));

const isSaving = ref(false);
const error = ref('');
const success = ref(false);

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      error.value = '';
      success.value = false;
    }
  },
);

async function submitToggle() {
  if (!props.store?.id || !props.module) return;
  isSaving.value = true;
  error.value = '';
  try {
    await StoreSettingsService.updateStoreSettings(props.store.id, {
      [props.module.key]: !props.currentValue,
    });
    success.value = true;
    emit('toggled');
    setTimeout(() => {
      if (success.value) close();
    }, 900);
  } catch (err: any) {
    const msgs = err?.response?.data?.message;
    error.value = Array.isArray(msgs)
      ? msgs.join(' · ')
      : msgs || t('modulesMgmt.toggleError');
  } finally {
    isSaving.value = false;
  }
}

function close() {
  emit('close');
}
</script>

<template>
  <Teleport to="body">
    <Transition name="mtd-backdrop">
      <div v-if="open" class="mtd-backdrop" @click="close" />
    </Transition>
    <Transition name="mtd-drawer">
      <aside v-if="open" class="mtd-drawer" @click.stop>
        <div class="mtd">
          <div class="mtd__header">
            <button class="mtd__back" @click="close">
              <i class="pi pi-times" />
            </button>
            <div class="mtd__header-info">
              <h1 class="mtd__name">{{ store?.designation ?? store?.name }}</h1>
              <p v-if="module" class="mtd__sub">{{ t(module.labelKey) }}</p>
            </div>
            <button
              class="mtd__view-store-btn"
              :title="t('modulesMgmt.viewStore')"
              @click="goToStore"
            >
              <i class="pi pi-external-link" />
            </button>
          </div>

          <div v-if="module" class="mtd__card">
            <div
              class="mtd__mod-ico"
              :class="{ 'mtd__mod-ico--on': currentValue }"
            >
              <i class="pi" :class="module.icon" />
            </div>
            <h3 class="mtd__mod-title">{{ t(module.labelKey) }}</h3>
            <p class="mtd__mod-desc">{{ t(module.descKey) }}</p>
            <span
              class="mtd__mod-badge"
              :class="
                currentValue ? 'mtd__mod-badge--on' : 'mtd__mod-badge--off'
              "
            >
              {{ currentValue ? t('modulesMgmt.on') : t('modulesMgmt.off') }}
            </span>

            <p v-if="success" class="mtd__success">
              <i class="pi pi-check-circle" />
              {{ t('modulesMgmt.toggleSuccess') }}
            </p>
            <p v-if="error" class="mtd__error">
              <i class="pi pi-exclamation-circle" /> {{ error }}
            </p>

            <div class="mtd__actions">
              <button class="mtd__btn mtd__btn--ghost" @click="close">
                {{ t('modulesMgmt.cancel') }}
              </button>
              <button
                class="mtd__btn"
                :class="currentValue ? 'mtd__btn--danger' : 'mtd__btn--primary'"
                :disabled="isSaving"
                @click="submitToggle"
              >
                <span v-if="isSaving" class="mtd__spinner" />
                {{
                  isSaving
                    ? t('modulesMgmt.saving')
                    : currentValue
                      ? t('modulesMgmt.deactivate')
                      : t('modulesMgmt.activate')
                }}
              </button>
            </div>
          </div>
        </div>
      </aside>
    </Transition>
  </Teleport>
</template>

<style scoped>
.mtd-backdrop {
  position: fixed;
  inset: 0;
  z-index: 900;
  background: rgba(8, 12, 20, 0.45);
  backdrop-filter: blur(2px);
}
.mtd-backdrop-enter-active,
.mtd-backdrop-leave-active {
  transition: opacity 0.2s ease;
}
.mtd-backdrop-enter-from,
.mtd-backdrop-leave-to {
  opacity: 0;
}

.mtd-drawer {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 901;
  width: min(420px, 100vw);
  background: #f4f6f9;
  box-shadow: -4px 0 30px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}
[data-theme='dark'] .mtd-drawer {
  background: #0c1421;
}
.mtd-drawer-enter-active,
.mtd-drawer-leave-active {
  transition: transform 0.25s ease;
}
.mtd-drawer-enter-from,
.mtd-drawer-leave-to {
  transform: translateX(100%);
}

.mtd {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1.75rem;
  box-sizing: border-box;
  overflow-y: auto;
}

.mtd__header {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 1.25rem;
  padding: 1.5rem;
  box-shadow: 0 4px 15px rgba(142, 142, 142, 0.06);
}
.mtd__back {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  flex-shrink: 0;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-surface-low);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
}
.mtd__view-store-btn {
  width: 2.25rem;
  height: 2.25rem;
  min-width: 2.25rem;
  border-radius: 50%;
  flex-shrink: 0;
  margin-left: auto;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-surface-low);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
}
.mtd__view-store-btn:hover {
  border-color: #ffd60a;
  color: #0b1320;
}
.mtd__header-info {
  min-width: 0;
}
.mtd__name {
  margin: 0 0 0.15rem;
  font-size: 1.0625rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
}
.mtd__sub {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}

.mtd__card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  text-align: center;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 1.25rem;
  padding: 2rem 1.5rem;
  box-shadow: 0 4px 15px rgba(142, 142, 142, 0.06);
}
.mtd__mod-ico {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface-muted);
  font-size: 1.375rem;
}
.mtd__mod-ico--on {
  background: rgba(16, 181, 16, 0.12);
  color: #0f7b0f;
}
.mtd__mod-title {
  margin: 0;
  font-size: 1.0625rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
}
.mtd__mod-desc {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
  line-height: 1.5;
}
.mtd__mod-badge {
  font-size: 0.625rem;
  font-weight: 800;
  padding: 0.2rem 0.625rem;
  border-radius: 0.4rem;
  letter-spacing: 0.05em;
}
.mtd__mod-badge--on {
  background: rgba(16, 181, 16, 0.15);
  color: #0f7b0f;
}
.mtd__mod-badge--off {
  background: var(--nxt-surface-mid);
  color: var(--nxt-on-surface-muted);
}

.mtd__success {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  padding: 0.625rem 0.875rem;
  border-radius: 0.75rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #0f7b0f;
  background: rgba(16, 181, 16, 0.12);
}
.mtd__error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  color: var(--nxt-error);
  margin: 0;
}

.mtd__actions {
  display: flex;
  gap: 0.75rem;
  width: 100%;
  margin-top: 0.5rem;
}
.mtd__btn {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  height: 2.5rem;
  padding: 0 1.25rem;
  border-radius: 0.625rem;
  font-size: 0.875rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  border: none;
}
.mtd__btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.mtd__btn--ghost {
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface-variant);
  border: 1px solid var(--nxt-outline-variant);
}
.mtd__btn--primary {
  background: #ffd60a;
  color: #0b1320;
  box-shadow: 0 2px 8px rgba(255, 214, 10, 0.35);
}
.mtd__btn--danger {
  background: var(--nxt-error);
  color: #fff;
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.35);
}
.mtd__spinner {
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  display: inline-block;
  border: 2px solid rgba(11, 19, 32, 0.2);
  border-top-color: #0b1320;
  animation: mtd-spin 0.7s linear infinite;
}
@keyframes mtd-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>

<style>
[data-theme='dark'] .mtd__header,
[data-theme='dark'] .mtd__card {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .mtd__back,
[data-theme='dark'] .mtd__view-store-btn,
[data-theme='dark'] .mtd__btn--ghost {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.7);
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .mtd__mod-ico {
  background: rgba(255, 255, 255, 0.06);
}
</style>
